'use client';

import { useState, type JSX } from 'react';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { Notification } from '@packages/shared/src/components/Notification/Notification';
import { hasBeenClosed } from './helpers/closed';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { NewsletterSheetOptimizely } from './NewsletterSheetOptimizely';

/**
 * The Newsletter Sheet Container Component:
 * A dialog at the bottom of the screen that is used to subscribe to the newsletter.
 *
 * It also checks your subscription state and hides itself if the user is already subscribed.
 * If the confirmation is still pending, a notification is displayed to remind the user.
 *
 * State:
 * - `forceHidden`: Boolean state to force hide the sheet.
 * - `notification`: Object containing notification state with `show` and `message`.
 *
 * Returns:
 * - `null` if the sheet should be hidden.
 * - A `Box` component containing the notification and the appropriate newsletter sheet component.
 */
const NewsletterSheet = () => {
  const { nlTexts } = useNlSheetTexts();
  const {
    forms: {
      newsletterSettings: { useDOI },
      newsletterSheet: { enabled: sheetEnabled },
    },
  } = useConfig();
  const { jwtToken } = useSession();
  const { loggedIn } = useLoginState();
  const closed = hasBeenClosed();

  const [forceHidden, setForceHidden] = useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string | JSX.Element;
  }>({ show: false, message: '' });
  const { setCookie, getCookies } = useCookies();

  const truncatedSheetToken = jwtToken?.slice(0, 30);
  const isNotificationOpen = getCookies()['news-sheet-disabled'] !== truncatedSheetToken;

  /**
   * Hide the sheet during login process, if it has been closed,
   * or it should be hidden for a logged in customer
   * */
  const hideSheet = forceHidden || !sheetEnabled || closed;

  if (hideSheet) {
    return null;
  }

  /**
   * Notification for pending DOI E-Mail.
   * This component is used instead of `addToast` to have a component as the message child.
   *
   * Note (INSPIRE-2154):
   * - The pending layer should only be displayed in the first 6 months after registration.
   * - To be precise, after the first declaration of intent in the DOI process.
   * - During this period, the layer should be displayed once per session for 15 seconds.
   */
  return (
    <>
      <Notification
        open={notification.show}
        severity="warning"
        autoHideDuration={15000}
        onClose={() => {
          if (jwtToken) {
            setCookie(null, 'news-sheet-disabled', `${truncatedSheetToken}`, {
              path: '/',
            });
          }

          setNotification({ ...notification, show: false });
        }}
      >
        {notification.message}
      </Notification>

      <NewsletterSheetOptimizely
        nlTexts={nlTexts}
        loggedIn={loggedIn}
        useDOI={useDOI}
        setNotification={setNotification}
        notification={notification}
        setForceHidden={setForceHidden}
        isNotificationOpen={isNotificationOpen}
      />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default NewsletterSheet;
